// src/pages/ShopMetafields.js
import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  DataTable,
  Button,
  Modal,
  FormLayout,
  TextField,
  Select,
  Banner,
  Stack,
  ButtonGroup,
  Text,
  Box,
  Icon,
  Tooltip
} from '@shopify/polaris';
import { DeleteMinor, EditMinor } from '@shopify/polaris-icons';
import { useShop } from '../contexts/ShopContext';

// Features:
// - List existing metafields
// - Add new metafields
// - Edit existing metafields
// - Delete metafields
// - Type selection (string, number, json_string, etc.)

export default function ShopMetafields() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { getShopMetafields, addShopMetafield, removeShopMetafield } = useShop();
  
  const [metafields, setMetafields] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingMetafield, setEditingMetafield] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [namespace, setNamespace] = useState('');
  const [key, setKey] = useState('');
  const [type, setType] = useState('single_line_text_field');

  const typeOptions = [
    { label: 'Single Line Text', value: 'single_line_text_field' },
    { label: 'Multi Line Text', value: 'multi_line_text_field' },
    { label: 'Number', value: 'number_integer' },
    { label: 'JSON String', value: 'json_string' }
  ];

  const loadMetafields = useCallback(async () => {
    try {
      const shopMetafields = await getShopMetafields(shopId);
      setMetafields(shopMetafields || []);
    } catch (err) {
      setError('Failed to load metafields');
    }
  }, [shopId, getShopMetafields]);

  useEffect(() => {
    loadMetafields();
  }, [loadMetafields]);

  const handleAddMetafield = async () => {
    try {
      if (!namespace.trim() || !key.trim()) {
        setError('Namespace and key are required');
        return;
      }

      const newMetafield = {
        namespace: namespace.trim(),
        key: key.trim(),
        type
      };

      await addShopMetafield(shopId, newMetafield);
      await loadMetafields();
      
      setSuccess('Metafield added successfully');
      setShowModal(false);
      resetForm();
    } catch (err) {
      setError(err.message || 'Failed to add metafield');
    }
  };

  const handleDeleteMetafield = async (namespace, key) => {
    try {
      await removeShopMetafield(shopId, namespace, key);
      await loadMetafields();
      setSuccess('Metafield deleted successfully');
    } catch (err) {
      setError(err.message || 'Failed to delete metafield');
    }
  };

  const handleEditMetafield = (metafield) => {
    setEditingMetafield(metafield);
    setNamespace(metafield.namespace);
    setKey(metafield.key);
    setType(metafield.type || 'single_line_text_field');
    setShowModal(true);
  };

  const resetForm = () => {
    setNamespace('');
    setKey('');
    setType('single_line_text_field');
    setEditingMetafield(null);
  };

  const rows = metafields.map((metafield) => [
    <Text variant="bodyMd">{metafield.namespace}</Text>,
    <Text variant="bodyMd">{metafield.key}</Text>,
    <Text variant="bodyMd">
      {typeOptions.find(t => t.value === metafield.type)?.label || metafield.type}
    </Text>,
    <ButtonGroup>
      <Tooltip content="Edit Metafield">
        <Button
          size="slim"
          onClick={() => handleEditMetafield(metafield)}
          icon={<Icon source={EditMinor} />}
        />
      </Tooltip>
      <Tooltip content="Delete Metafield">
        <Button
          size="slim"
          destructive
          onClick={() => handleDeleteMetafield(metafield.namespace, metafield.key)}
          icon={<Icon source={DeleteMinor} />}
        />
      </Tooltip>
    </ButtonGroup>
  ]);

  return (
    <Page
      title="Shop Metafields"
      breadcrumbs={[{ content: 'Shops', onAction: () => navigate('/app/shops') }]}
      primaryAction={{
        content: 'Add Metafield',
        onAction: () => {
          resetForm();
          setShowModal(true);
        },
      }}
    >
      {error && (
        <Box paddingBlockEnd="4">
          <Banner status="critical">{error}</Banner>
        </Box>
      )}
      {success && (
        <Box paddingBlockEnd="4">
          <Banner status="success">{success}</Banner>
        </Box>
      )}

      <Card>
        <DataTable
          columnContentTypes={['text', 'text', 'text', 'text']}
          headings={['Namespace', 'Key', 'Type', 'Actions']}
          rows={rows}
        />
      </Card>

      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editingMetafield ? 'Edit Metafield' : 'Add Metafield'}
        primaryAction={{
          content: editingMetafield ? 'Save Changes' : 'Add Metafield',
          onAction: handleAddMetafield,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowModal(false);
              resetForm();
            },
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Namespace"
              value={namespace}
              onChange={setNamespace}
              error={!namespace.trim() && 'Namespace is required'}
            />
            <TextField
              label="Key"
              value={key}
              onChange={setKey}
              error={!key.trim() && 'Key is required'}
            />
            <Select
              label="Type"
              options={typeOptions}
              value={type}
              onChange={setType}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}